import React from 'react';
import { useParams } from 'react-router-dom';
import { Button, ControlLink, Stack, Text } from '@moda/om';
import { hrefFor } from '../../routers';
import { useReturn } from '../../hooks/useReturn';
import { useReturnSummary } from '../../hooks/useReturnSummary';

import './ReturnInstructions.scss';

enum ReturnOption {
  UpsDropOff,
  UpsPickUp,
  DhlDropOff
}
interface Props {
  forDetails?: boolean;
  forConfirmation?: boolean;
}

export const ReturnInstructions: React.FC<Props> = ({ forDetails, forConfirmation }) => {
  const { shipmentNumber } = useParams<{ shipmentNumber: string }>();
  const { returnOrder } = useReturn(shipmentNumber);
  const { returnSummary } = useReturnSummary({ shipmentNumber });

  const returnDate =
    returnOrder &&
    new Date(returnOrder.attributes.createdAt).toLocaleDateString('us-EN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });

  if (!returnSummary) return null;

  return (
    <Stack space={3} className="ReturnInstructions">
      {forDetails && (
        <Text treatment="h3" family="serif">
          Refund Details
        </Text>
      )}

      {forConfirmation && (
        <Text treatment="h3" family="serif">
          Your return request is processed!
        </Text>
      )}

      <Text>Return Authorization: #{returnSummary.attributes.shipmentNumber}</Text>
      {forDetails && (
        <Stack space={2} direction="horizontal">
          <Text>Order Number: </Text>
          <Text color="cement">
            <ControlLink
              to={hrefFor.OrderDetailPage({ orderNumber: returnSummary.attributes.orderNumber })}
              underlined={false}
            >
              #{returnSummary.attributes.orderNumber}
            </ControlLink>
          </Text>
        </Stack>
      )}

      {forDetails && <Text>Return Date: {returnDate}</Text>}

      <Button
        className="ReturnInstructions__label-button"
        href={returnOrder?.attributes.shippingLabel}
        target="_blank"
        rel="noopener noreferrer"
      >
        PRINT SHIPPING LABEL
      </Button>

      {returnSummary.attributes.returnOptionId === ReturnOption.UpsDropOff && (
        <Text color="cement">
          Find a UPS location{' '}
          <ControlLink href="https://www.ups.com/dropoff" target="_blank" rel="noopener noreferrer">
            near you
          </ControlLink>{' '}
          and drop off your return with this prepaid shipping label. A copy will also be emailed to
          you.
        </Text>
      )}

      {returnSummary.attributes.returnOptionId === ReturnOption.UpsPickUp && (
        <Text color="cement">
          Schedule a pick up time either online or call 1-800-742-5877. Use account #3Y04R3 with
          billing zipcode 10013 when prompted to receive a free pick up. If you can’t make the pick
          up, you can always drop off your return at the{' '}
          <ControlLink href="https://www.ups.com/pickup" target="_blank" rel="noopener noreferrer">
            nearest
          </ControlLink>{' '}
          UPS location.
        </Text>
      )}

      {returnSummary.attributes.returnOptionId === ReturnOption.DhlDropOff && (
        <Text color="cement">
          Find a DHL location near you and drop off your return with this prepaid shipping label. A
          copy will also be emailed to you. To schedule a pickup, please contact your local DHL
          office and reference the DHL WayBill number on your shipping label along with the phone
          number +1 (212) 729-0984 .
        </Text>
      )}

      {forDetails && (
        <ControlLink className="ReturnInstructions__link" to={hrefFor.AccountReturns()}>
          View All Requests
        </ControlLink>
      )}
    </Stack>
  );
};
